import createDonutChartOptions, { ChartDatum } from 'lib/agCharts/donutChart';
import { currenciesMap } from 'lib/constants/currencies';
import { EventWaitingLine } from 'lib/graphql/entities/waitingLines/types';
import { Languages } from 'lib/i18n/i18n';
import useEvent from 'providers/event/useEvent';
import LanguageContext from 'providers/i18n/LanguageContext';
import { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ChartCard from '../ChartCard/ChartCard';
import useFinancialDataQuery from './useFinancialDataQuery';

interface WaitingLinesSalesChartProps {
  currency: string
  selectedTime: string
  selectedWaitingLine: string
  waitingLinesMap: Record<string, EventWaitingLine>
}

function WaitingLinesSalesChart(props: WaitingLinesSalesChartProps) {
  const {
    currency,
    selectedTime,
    selectedWaitingLine,
    waitingLinesMap,
  } = props;

  const { languages } = useEvent();
  const { language } = useContext(LanguageContext);
  const { formatMessage } = useIntl();

  const [
    aggregates,
    {
      loading,
      hasNextPage,
    },
  ] = useFinancialDataQuery(selectedTime, selectedWaitingLine);

  const defaultLanguage = languages[0] as Languages;
  const chartOptions = useMemo(() => {
    if (aggregates) {
      const symbol = currenciesMap[currency.toUpperCase()]?.symbol || '$';
      const chartData = aggregates.map<ChartDatum>((aggregate) => {
        const name = waitingLinesMap[aggregate.waitingLineId]?.name || { [defaultLanguage]: '' };
        return {
          amount: aggregate.eventAmount / 100,
          asset: (name[language] || name[defaultLanguage]) as string,
          unit: symbol,
        };
      });
      return createDonutChartOptions(chartData, language, formatMessage({ id: 'your_balance' }));
    }
    return {};
  }, [aggregates, currency, defaultLanguage, formatMessage, language, waitingLinesMap]);

  return (
    <ChartCard
      title={<FormattedMessage id="total_sales_queue" />}
      loading={loading || hasNextPage}
      chartOptions={chartOptions}
    />
  );
}

export default WaitingLinesSalesChart;
